/**
 * A VISIT IS A RECORD. IT IS NOT A VIEW.
 *
 * Everything else in this product that looks like a status is computed: a
 * card's column on the board, whether a section is measured, whether a scope
 * has drifted. None of those are fields, because a field can disagree with
 * what actually happened.
 *
 * A visit is the opposite and has to be. Nothing in the product implies the
 * date somebody is coming; a person decides it, and the decision is the fact.
 * So this is the first real writable record in LightsOS, and it carries the
 * safeguards the contract asks for rather than being a date on a job.
 *
 * THE THREE RULES, FROM THE DATA CONTRACT AND SCHED-01/02.
 *
 *   A customer preference is not a confirmed schedule. Asked-for and agreed
 *   are different states and the screen never shows one as the other.
 *
 *   No silent timezone conversion. A window is the wall clock at the house.
 *   These are stored as a plain date and plain minutes past midnight, never as
 *   an instant, because an instant is a lie about a job at a specific address
 *   unless you also store the zone, and nothing in this product has one.
 *
 *   A suggestion is not a confirmed appointment. Nothing here proposes a slot.
 *
 * WHAT IS DELIBERATELY MISSING, AND SAYS SO. Crews, trucks, skills, travel
 * time, equipment readiness and material readiness are all in the contract for
 * this screen and none of them exist in this product. A conflict check that
 * silently skipped them would be the most dangerous thing on the screen: it
 * would look like it had checked. So conflicts() reports what it checked AND
 * what it could not, and the screen prints both.
 */

/** Estimate, install, service, takedown. The four in the contract. */
export type VisitKind = 'estimate' | 'install' | 'service' | 'takedown'

export const VISIT_KINDS: { id: VisitKind; label: string; blurb: string }[] = [
  { id: 'estimate', label: 'Estimate', blurb: 'Walk the house and measure it.' },
  { id: 'install', label: 'Install', blurb: 'Put the lights up.' },
  { id: 'service', label: 'Service', blurb: 'Something needs fixing.' },
  { id: 'takedown', label: 'Takedown', blurb: 'Bring it all back down.' },
]

/**
 * Asked for, or agreed. The contract is explicit that a customer preference is
 * not a schedule, and the whole reason to keep two states is that a dispatcher
 * looking at a week has to know which of these he can rely on.
 */
export type VisitState = 'requested' | 'confirmed'

export interface Visit {
  id: string
  jobId: string
  kind: VisitKind
  /** YYYY-MM-DD, the day at the house. Never an instant. */
  day: string
  /** Minutes past midnight, local to the house. Eight in the morning is 480. */
  from: number
  /** Minutes past midnight. Must be after from. */
  to: number
  state: VisitState
  /** Free text: gate code, dog, where the ladder goes. */
  note: string
  /** When this record was written, for ordering only. */
  at: string
  /**
   * WHEN SOMEBODY ACTUALLY TURNED UP, AND WHEN THEY LEFT.
   *
   * A separate axis from state, deliberately. Asked for and agreed are about
   * whether a thing was arranged; these are about whether it happened, and a
   * visit can be agreed and never happen or happen having never been agreed at
   * all. Folding them into one field is how a calendar ends up claiming a crew
   * was at a house because somebody confirmed the appointment on the phone.
   *
   * Both absent on every visit booked before this existed, and on every visit
   * nobody has started. Written only by a person pressing a button while
   * standing there, never derived from the clock passing the window.
   */
  startedAt?: string
  doneAt?: string
  /**
   * WHO IS GOING. Crew member ids, from lib/crew.ts.
   *
   * Empty or absent means nobody has said, which is different from nobody
   * going, and the conflict check below treats those as different things.
   * Names are not stored here: a person renamed on the roster would otherwise
   * stay spelled the old way on every visit they were ever on.
   */
  assignedTo?: string[]
}

/** Two digits, because five past eight is not 8:5. */
const pad = (n: number): string => (n < 10 ? `0${n}` : String(n))

/** 480 becomes 8:00 am. Wall clock at the house, no zone applied. */
export function clock(min: number): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440
  const h24 = Math.floor(m / 60)
  const mm = m % 60
  const ampm = h24 < 12 ? 'am' : 'pm'
  const h = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h}:${pad(mm)} ${ampm}`
}

/** Eight to eleven thirty. One string, because a window is one fact. */
export const windowOf = (v: Visit): string => `${clock(v.from)} to ${clock(v.to)}`

/** A span of minutes, said the way a person says it. */
export function spanOf(mins: number): string {
  const n = Math.max(0, Math.round(mins))
  const h = Math.floor(n / 60)
  const m = n % 60
  if (h === 0) return `${m} min`
  if (m === 0) return h === 1 ? '1 hour' : `${h} hours`
  return `${h}h ${m}m`
}

/** How long the window is, said the way a person says it. */
export const lengthOf = (v: Visit): string => spanOf(v.to - v.from)

/**
 * MONDAY OF THE WEEK THIS DAY FALLS IN.
 *
 * Built off the plain date parts in UTC rather than a Date's local calendar,
 * so a machine set to a different zone than the houses cannot shift the week
 * by a day. new Date('2026-09-07') is midnight UTC and prints as the sixth in
 * Chicago, which is exactly the class of bug this product cannot have on a
 * screen about which day somebody is coming.
 */
export function weekStart(day: string): string {
  const [y, m, d] = day.split('-').map(Number)
  const at = new Date(Date.UTC(y, m - 1, d))
  const dow = at.getUTCDay() // 0 is Sunday
  const back = dow === 0 ? 6 : dow - 1
  at.setUTCDate(at.getUTCDate() - back)
  return at.toISOString().slice(0, 10)
}

/** The seven days of that week, as plain dates. */
export function weekDays(day: string): string[] {
  const [y, m, d] = weekStart(day).split('-').map(Number)
  const out: string[] = []
  for (let i = 0; i < 7; i++) {
    const at = new Date(Date.UTC(y, m - 1, d + i))
    out.push(at.toISOString().slice(0, 10))
  }
  return out
}

/** Shift a week by n weeks, keeping to plain dates. */
export function shiftWeek(day: string, n: number): string {
  const [y, m, d] = weekStart(day).split('-').map(Number)
  const at = new Date(Date.UTC(y, m - 1, d + n * 7))
  return at.toISOString().slice(0, 10)
}

/** Mon, Sep 7. No zone conversion anywhere in it. */
export function dayLabel(day: string): string {
  const [y, m, d] = day.split('-').map(Number)
  const at = new Date(Date.UTC(y, m - 1, d))
  return at.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

export const dayNumber = (day: string): string => day.slice(8)

/** What a visit is on, for a queue that has no job objects in it. */
export interface JobRef {
  id: string
  customer: string
  address: string
  name: string
  /** True when a scope has been agreed. See lib/approval.ts. */
  scopeApproved: boolean
}

/** Two visits on one day whose windows sit on top of each other. */
export function overlaps(a: Visit, b: Visit): boolean {
  if (a.id === b.id) return false
  if (a.day !== b.day) return false
  return a.from < b.to && b.from < a.to
}

/**
 * WHETHER THE SAME PERSON IS DOWN FOR BOTH, AND WHETHER ANYBODY KNOWS.
 *
 * Three answers, not two. Two visits assigned to different people at the same
 * hour are not a conflict and never were; two visits with nobody named on them
 * might be, and nothing here can tell, which is a different thing from safe.
 * Collapsing unknown into either of the other two is how a check like this
 * either cries wolf on every second booking or misses the one that matters.
 */
export type CrewOverlap = 'shared' | 'separate' | 'unknown'

export function sharesCrew(a: Visit, b: Visit): CrewOverlap {
  const x = a.assignedTo ?? []
  const y = b.assignedTo ?? []
  if (x.length === 0 || y.length === 0) return 'unknown'
  return x.some((id) => y.includes(id)) ? 'shared' : 'separate'
}

/** One visit this lands on top of, and why that is or is not a problem. */
export interface Clash {
  visit: Visit
  crew: CrewOverlap
  /** The ids down for both. Empty unless crew is shared. */
  shared: string[]
}

/** Everything already booked that this one would sit on top of. */
export function clashesWith(v: Visit, all: Visit[]): Clash[] {
  const out: Clash[] = []
  for (const o of all) {
    if (!overlaps(v, o)) continue
    const crew = sharesCrew(v, o)
    // Different people at the same hour is two crews working, not a clash.
    if (crew === 'separate') continue
    const mine = v.assignedTo ?? []
    out.push({ visit: o, crew, shared: (o.assignedTo ?? []).filter((id) => mine.includes(id)) })
  }
  return out
}

/** A conflict check that reports its own blind spots. */
export interface ConflictReport {
  /** Visits this one lands on top of, with the reason each one counts. */
  clashes: Clash[]
  /** Ids named on this visit who are down as away that day. */
  awayIds: string[]
  /** Things the contract asks for that this product cannot check yet. */
  notChecked: string[]
  /** True when nothing was found. It never means nothing could go wrong. */
  clean: boolean
}

/**
 * The unchecked list is not an apology, it is the point. SCHED-02 asks for
 * double booking, travel and equipment to block or need an override with a
 * reason. A screen that showed a green tick for what it cannot see would be
 * lying in the way that costs a Saturday.
 *
 * WHETHER A CREW IS FREE CAME OFF THIS LIST when the roster shipped. A visit
 * can name who is going now, so that one is checked rather than assumed, and
 * a line that has been true since the day this file was written stops being
 * true. The two that are left have nothing behind them at all.
 */
export const NOT_CHECKED: string[] = [
  'Travel time between houses. There is no route or address distance behind it.',
  'Whether the materials are on the truck. There is no stock count yet.',
]

/**
 * Takes an away test rather than the roster itself, so this file keeps its own
 * plain-date rules and never imports lib/crew.ts.
 */
export function conflicts(
  v: Visit,
  all: Visit[],
  isAway: (crewId: string, day: string) => boolean = () => false,
): ConflictReport {
  const clashes = clashesWith(v, all)
  const awayIds = (v.assignedTo ?? []).filter((id) => isAway(id, v.day))
  return {
    clashes,
    awayIds,
    notChecked: NOT_CHECKED,
    clean: clashes.length === 0 && awayIds.length === 0,
  }
}

/**
 * WHAT IS WAITING TO BE PUT ON THE CALENDAR.
 *
 * A job with an agreed scope and no install booked is the queue, in that
 * order, because that is the one a season is lost by forgetting. A job with no
 * scope yet is not queued for install: nobody has agreed what the install is.
 */
export interface Queued {
  job: JobRef
  /** Why it is in the queue, in the words that go on the row. */
  said: string
  kind: VisitKind
}

export function unscheduled(jobs: JobRef[], visits: Visit[]): Queued[] {
  const has = (jobId: string, kind: VisitKind) =>
    visits.some((v) => v.jobId === jobId && v.kind === kind)

  const out: Queued[] = []
  for (const job of jobs) {
    if (job.scopeApproved && !has(job.id, 'install')) {
      out.push({ job, kind: 'install', said: 'Scope agreed, no install booked.' })
      continue
    }
    if (!job.scopeApproved && !has(job.id, 'estimate') && !has(job.id, 'install')) {
      out.push({ job, kind: 'estimate', said: 'Nothing agreed and nobody has walked it.' })
    }
  }
  return out
}

/**
 * WHAT IS HAPPENING TO A VISIT RIGHT NOW.
 *
 * Four answers and only one of them is a clock reading: booked, on site,
 * finished, and a window that has gone by with nobody having pressed
 * anything. That last one is not a failure and is never presented as one; it
 * is the ordinary state of a visit somebody drove to and forgot to tap.
 */
export type StopState = 'booked' | 'onsite' | 'done' | 'unmarked'

export function stopState(v: Visit, now: { day: string; minutes: number }): StopState {
  if (v.doneAt) return 'done'
  if (v.startedAt) return 'onsite'
  if (v.day < now.day || (v.day === now.day && now.minutes > v.to)) return 'unmarked'
  return 'booked'
}

/** How long somebody was there, off the two stamps rather than the window. */
export function timeOnSite(v: Visit): number | null {
  if (!v.startedAt || !v.doneAt) return null
  const a = Date.parse(v.startedAt)
  const b = Date.parse(v.doneAt)
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null
  return Math.round((b - a) / 60000)
}

/**
 * THE DAY AN INSTALL WAS FINISHED AT THIS HOUSE, or nothing.
 *
 * The first thing in this product that records a job as done. Latest finished
 * install wins, because a house that was added to in December was finished in
 * December. Only installs: a service call in January does not restart the
 * clock on a balance that came due in November.
 */
export function finishedOn(visits: Visit[], kind: VisitKind = 'install'): string {
  let best = ''
  for (const v of visits) {
    if (v.kind !== kind || !v.doneAt) continue
    const d = v.doneAt.slice(0, 10)
    if (d > best) best = d
  }
  return best
}

/**
 * WHAT A CREW SCREEN CANNOT SAY, because none of it exists.
 *
 * TOD-02 asks for only assigned work to be visible, a route with a timestamped
 * ETA, and an offline packet with a download step. There is one login, no map
 * and no separate packet, so all three would be theatre.
 */
export const NOT_ON_THE_STOP: string[] = [
  'Who it is assigned to. There is one login and no crew records, so everything booked is yours.',
  'A route or a drive time. There is no map behind this and no address distances.',
  'A packet to download. Jobs and photographs are already kept on this device, so there is nothing separate to fetch before leaving.',
]

/** Every visit on one day, earliest first. */
export const onDay = (day: string, visits: Visit[]): Visit[] =>
  visits.filter((v) => v.day === day).sort((a, b) => a.from - b.from)

/**
 * A NEW VISIT, WITH A DEFAULT THAT IS NOT A SUGGESTION.
 *
 * Eight in the morning for three hours is a starting point for a form, not a
 * proposal about this house, and it arrives as requested rather than
 * confirmed so that nothing is ever agreed by the act of creating it.
 */
export function newVisit(input: {
  id: string
  jobId: string
  kind: VisitKind
  day: string
  at: string
}): Visit {
  return {
    id: input.id,
    jobId: input.jobId,
    kind: input.kind,
    day: input.day,
    from: 8 * 60,
    to: 11 * 60,
    state: 'requested',
    note: '',
    at: input.at,
  }
}

/** A window that runs backwards is not a window. */
export function validWindow(from: number, to: number): boolean {
  return Number.isFinite(from) && Number.isFinite(to) && from >= 0 && to <= 1440 && to > from
}

/** What the state means, said out loud, because the two are easy to confuse. */
export function stateWording(v: Visit): string {
  return v.state === 'confirmed'
    ? 'Agreed with the customer.'
    : 'Asked for, not agreed yet. This is not a booking.'
}
